import { useEffect, useState } from "react";
import { hasMath, renderMathInHtml } from "./math.js";

/**
 * Renders generated HTML inside an isolated document, not directly into the
 * app's own DOM.
 *
 * Rendering with dangerouslySetInnerHTML straight into a <div> puts the
 * content in the same document as ZenForge's own stylesheet: a category's
 * <style> block becomes a page-wide rule (not scoped to that div), and
 * ZenForge's own h2/p/table/.preview rules apply to the generated content on
 * top of it. Neither side designed for that cascade, so a sample built as a
 * standalone themed page never renders the way it was designed — this is
 * why "the preview doesn't show the exact UI".
 *
 * An iframe with its own document has its own CSSOM: the category's CSS
 * applies exactly as authored, and nothing from ZenForge's stylesheet leaks
 * in either direction.
 *
 * Generated HTML is rendered exactly as the model produced it. The DOM
 * sanitizer that used to run first was removed (2026-08-21, deliberate call):
 * the pipeline is pass-through end to end, so what a reviewer sees here is
 * byte-for-byte what was stored and what gets published.
 *
 * That makes `sandbox="allow-scripts allow-forms allow-modals"` the containment
 * boundary: model-authored <script> executes safely inside its own isolated
 * browsing context, while omitting `allow-same-origin` guarantees the iframe
 * has an opaque `null` origin. It cannot access the host window (window.parent),
 * cookies, localStorage, or app state.
 */
function buildDocument(rawHtml: string): string {
  // If the content is already a complete HTML document, do not wrap it into another html/body.
  if (/^\s*<!doctype/i.test(rawHtml) || /^\s*<html/i.test(rawHtml)) {
    return rawHtml;
  }
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  /* Neutral base so content with no styling of its own is still readable —
     a category's own CSS overrides this normally through the cascade. */
  body { margin: 0; padding: 20px; font-family: system-ui, sans-serif; color: #1b1f27; }
</style>
</head>
<body>${rawHtml}</body>
</html>`;
}

export function HtmlPreview({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  // LaTeX is converted to MathML before the document is built. The models
  // emit \(…\) and \[…\] throughout generated physics content, and without
  // this it reaches the iframe as literal backslashes and braces.
  const srcDoc = buildDocument(hasMath(html) ? renderMathInHtml(html) : html);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  return (
    <>
      <div style={{ position: "relative" }}>
        <button
          className="btn mini"
          onClick={() => setExpanded(true)}
          style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        >
          ⛶ Full screen
        </button>
        <iframe
          title="Generated content preview"
          srcDoc={srcDoc}
          sandbox="allow-scripts allow-forms allow-modals"
          style={{
            width: "100%",
            // Matches the Review editor's height, so switching between the
            // Preview and Edit tabs does not resize the panel under you.
            // Full screen is still there for a proper read.
            height: "60vh",
            minHeight: 420,
            border: "1px solid var(--line)",
            borderRadius: 10,
            background: "#fff",
          }}
        />
      </div>

      {expanded && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setExpanded(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(24,32,51,0.6)",
            zIndex: 100,
            display: "flex",
            flexDirection: "column",
            padding: 24,
          }}
        >
          <div className="row between" style={{ marginBottom: 12 }}>
            <span style={{ color: "#fff", fontWeight: 600 }}>
              Preview — press Esc or click outside to close
            </span>
            <button className="btn" onClick={() => setExpanded(false)}>
              Close
            </button>
          </div>
          <iframe
            title="Generated content preview — full screen"
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-forms allow-modals"
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              width: "100%",
              border: "none",
              borderRadius: 10,
              background: "#fff",
            }}
          />
        </div>
      )}
    </>
  );
}
